import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, Award, BookPlus, CalendarDays, Check, ChevronDown, ChevronUp, Construction, FileQuestion, Flag,
  LayoutGrid, Map as MapIcon, MapPin, Pencil, Play, Repeat2, Server, Target, Ticket, Timer, Video,
} from 'lucide-react';
import { apiErrorMessage, trailService } from '../services/api';
import {
  Breadcrumbs, Button, EmptyState, GlobalShell, Loader, ScoreCard, SectionTitle,
} from '../components/ui';
import {
  AvailabilityModal, SealPill, StatusPill, TypePill, WeightBar,
} from '../components/trails/TrailBits';
import { PARTS, PART_STATUS, TRAIL_TYPES, fmtHours, fmtHoursRound, stageNumber, unlockHint } from '../utils/trails';
import './TrailView.css';

const ACCESS_ICONS = [[/vm|máquina/i, Server], [/sess|ao vivo|live/i, Video], [/voucher/i, Ticket]];
const accessIcon = (text) => (ACCESS_ICONS.find(([re]) => re.test(text)) || [null, Check])[1];

const COMP_PREVIEW = 4;

/** Ficha do destino (placar), mapa das etapas, pesos do exame e complementares. */
function TrailView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [trail, setTrail] = useState(null);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [allComp, setAllComp] = useState(false);
  const [sibling, setSibling] = useState(null);

  const load = useCallback(() => {
    setError(null);
    trailService.detail(slug).then(setTrail).catch(err => setError(err?.response?.status === 404 ? 'notfound' : apiErrorMessage(err)));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const hasStages = Boolean(trail?.stages.some(s => s.kind === 'stage'));

  // Sem etapas: sugere uma trilha do mesmo tipo que já tem plano
  useEffect(() => {
    if (!trail || hasStages) return;
    trailService.catalog({ type: trail.type })
      .then(d => setSibling(d.trails.find(t => t.hasStages && t.slug !== trail.slug) || null))
      .catch(() => setSibling(null));
  }, [trail, hasStages]);

  if (error) {
    return (
      <GlobalShell>
        <EmptyState
          file="trilha.xml"
          title={error === 'notfound' ? 'Trilha não encontrada' : 'Não foi possível abrir a trilha'}
          description={error === 'notfound' ? 'Ela pode ter saído do catálogo.' : error}
          action={<Button onClick={() => navigate('/trilhas')}>Ver trilhas</Button>}
        />
      </GlobalShell>
    );
  }
  if (!trail) return <GlobalShell><Loader label="Abrindo trilha..." /></GlobalShell>;

  const en = trail.enrollment;
  const openStage = (s) => navigate(`/trilhas/${slug}/etapas/${stageNumber(s)}`);
  const reached = reachedUntil(trail.stages);
  const planDays = en ? en.totalDays : trail.days;

  const stats = hasStages
    ? [
      trail.questions != null && { icon: FileQuestion, value: trail.questions, label: 'questões' },
      trail.minutes != null && { icon: Timer, value: trail.minutes, label: 'minutos' },
      trail.passPct != null && { icon: Target, value: `${trail.passPct}%`, label: 'nota de corte', tone: 'lime' },
      trail.attempts != null && { icon: Ticket, value: trail.attempts, label: 'tentativas no voucher' },
      { icon: CalendarDays, value: planDays, label: `dias · ${fmtHoursRound(trail.totalMinutes)} de plano` },
    ]
    : [
      trail.questions != null && { icon: FileQuestion, value: trail.questions, label: 'questões' },
      trail.minutes != null && { icon: Timer, value: trail.minutes, label: 'minutos' },
      trail.passPct != null && { icon: Target, value: `${trail.passPct}%`, label: 'nota de corte', tone: 'lime' },
      trail.areas.length > 0 && { icon: LayoutGrid, value: trail.areas.length, label: 'áreas do exame' },
    ];

  const groups = weightGroups(trail.examTopics);
  const comp = allComp ? trail.complementary : trail.complementary.slice(0, COMP_PREVIEW);
  const compRest = trail.complementary.length - COMP_PREVIEW;

  return (
    <GlobalShell trail={trail}>
      <div className="ss-page ss-page--wide ss-page--crumbs">
        <div className="trv-crumbs">
          <Breadcrumbs items={[
            { label: 'Trilhas', onClick: () => navigate('/trilhas') },
            { label: TRAIL_TYPES[trail.type]?.label || 'Trilha', onClick: () => navigate(`/trilhas?tipo=${trail.type}`) },
            { label: trail.shortTitle },
          ]} />
          {trail.canEdit && <Button variant="ghost" size="sm" icon={Pencil} onClick={() => navigate(`/trilhas/${slug}/editar`)} data-tour="trilha-editar">Editar trilha</Button>}
        </div>

        <div className="trv-hero">
          <ScoreCard
            inline
            stripe
            top={(
              <div className="trv-hero__top">
                <div className="trv-hero__main">
                  <div className="trv-hero__pills">
                    <span className="trv-destpill"><Flag size={12} strokeWidth={2.6} aria-hidden="true" />Destino</span>
                    <TypePill type={trail.type} onDark />
                    {trail.examCode && <code className="trv-hero__code">{trail.examCode}</code>}
                    {trail.status === 'draft' && <StatusPill status="pending" label="Rascunho" />}
                  </div>
                  <h1 className="trv-hero__title">{trail.title}</h1>
                  {trail.description && <p className="trv-hero__desc">{trail.description}</p>}
                </div>
                {hasStages && (en ? (
                  <Progress en={en} totalMinutes={trail.totalMinutes} onPlan={() => navigate(`/trilhas/${slug}/plano`)} />
                ) : (
                  <div className="trv-hero__cta">
                    <Button variant="accent" icon={Play} onClick={() => setEnrolling(true)} disabled={trail.status !== 'published'}>
                      Começar trilha
                    </Button>
                    <span>Escolha horas/dia e a data da prova</span>
                  </div>
                ))}
              </div>
            )}
            items={stats.filter(Boolean)}
          />
          {trail.accessNote && (
            <div className="trv-hero__access">
              <span>Acesso pela <strong>{trail.accessNote}</strong></span>
              {trail.accessItems.map(a => {
                const Icon = accessIcon(a);
                return <span key={a} className="trv-hero__item"><Icon size={14} aria-hidden="true" />{a}</span>;
              })}
            </div>
          )}
        </div>

        {hasStages ? (
          <div className="trv-grid">
            <section>
              <SectionTitle
                icon={MapIcon}
                aside={`${trail.stageCount} ${trail.stageCount === 1 ? 'etapa' : 'etapas'} · ${planDays} dias · ${fmtHoursRound(trail.totalMinutes)}`}
              >
                Mapa da trilha
              </SectionTitle>
              <ol className="trv-map" data-tour="trilha-etapas">
                {trail.stages.map((s, i) => (
                  <MapNode
                    key={s.id}
                    stage={s}
                    first={i === 0}
                    last={i === trail.stages.length - 1}
                    reached={reached >= i}
                    onOpen={['start', 'stage', 'final'].includes(s.kind) ? () => openStage(s) : s.kind === 'exam' ? () => navigate(`/trilhas/${slug}/prova`) : null}
                  />
                ))}
              </ol>
            </section>
            <aside className="trv-side">
              {trail.examTopics.length > 0 && (
                <section className="trv-card">
                  <div className="trv-card__head"><h2>Pesos do exame</h2><span>{trail.examTopics.length} tópicos</span></div>
                  <WeightBar weights={[...trail.examTopics].sort((a, b) => (a.stage ?? 99) - (b.stage ?? 99))} />
                  <div className="trv-weights">
                    {groups.map(g => (
                      <div key={g.key} className="trv-weights__group">
                        <span className="trv-weights__stage">
                          <span className="trv-sq" style={{ background: g.color }} />{g.label}
                        </span>
                        {g.items.map(t => (
                          <div key={t.name} className="trv-weights__row"><span>{t.name}</span><code>{t.label}</code></div>
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {trail.complementary.length > 0 && (
                <section className="trv-card">
                  <div className="trv-card__head"><h2>Complementares</h2><span>fora do plano</span></div>
                  <p className="trv-card__note">Aprofundam tópicos que costumam cair.</p>
                  <ul className="trv-comp">
                    {comp.map(c => (
                      <li key={c.id}>
                        <BookPlus size={13} aria-hidden="true" />
                        <span>{c.title}</span>
                        {c.done && <Check size={14} className="trv-comp__done" aria-label="feito" />}
                      </li>
                    ))}
                  </ul>
                  {compRest > 0 && (
                    <button type="button" className="trv-more" onClick={() => setAllComp(v => !v)} aria-expanded={allComp}>
                      {allComp ? 'Ver menos' : `Ver mais ${compRest}`}
                      {allComp ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
                    </button>
                  )}
                </section>
              )}
            </aside>
          </div>
        ) : (
          <>
            <div className="trv-warn" role="note">
              <Construction size={18} aria-hidden="true" />
              <span className="trv-warn__text">
                <strong>Esta trilha ainda não tem etapas nem plano de horas</strong>
                {trail.hint && <span>{trail.hint}</span>}
              </span>
              {sibling && (
                <Button size="sm" icon={ArrowRight} onClick={() => navigate(`/trilhas/${sibling.slug}`)}>
                  Ver trilha de {sibling.shortTitle || sibling.title}
                </Button>
              )}
            </div>
            {trail.areas.length > 0 && (
              <section className="ss-section trv-areasec">
                <SectionTitle icon={LayoutGrid} aside={`${trail.areas.length} ${trail.areas.length === 1 ? 'área' : 'áreas'}`}>Áreas do exame</SectionTitle>
                <div className="trv-areas">
                  {trail.areas.map((a, i) => (
                    <div key={a} className="trv-area">
                      <span className="trv-area__num">{String(i + 1).padStart(2, '0')}</span>
                      <span>{a}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      <AvailabilityModal open={enrolling} trail={trail} onClose={() => setEnrolling(false)} onDone={() => { setEnrolling(false); load(); }} />
    </GlobalShell>
  );
}

/** Trilha iniciada: o progresso fica no lugar do CTA, dentro do topo navy. */
function Progress({ en, totalMinutes, onPlan }) {
  return (
    <div className="trv-progress">
      <span className="trv-progress__eyebrow">Seu progresso</span>
      <span className="trv-progress__pct"><strong>{en.pct}%</strong>{fmtHoursRound(en.doneMinutes)} de {fmtHoursRound(totalMinutes)}</span>
      <span className="trv-progress__bar"><span style={{ width: `${en.pct}%` }} /></span>
      <span className="trv-progress__rows">
        <span>Dia {en.day} de {en.totalDays}</span>
        <span>Prova {en.examLabel ? `${en.examLabel} · ${dayCount(en.daysToExam)}` : 'sem data'}</span>
        <span className={en.lateCheckpoints ? 'is-late' : ''}>
          {en.lateCheckpoints ? `${en.lateCheckpoints} ${en.lateCheckpoints === 1 ? 'checkpoint atrasado' : 'checkpoints atrasados'}` : 'sem atrasos'}
        </span>
      </span>
      <Button variant="accent" size="sm" icon={Play} onClick={onPlan}>Plano de hoje</Button>
    </div>
  );
}

/** Tópicos do exame agrupados pela etapa que cobre cada um. */
function weightGroups(topics) {
  const map = new Map();
  topics.forEach(t => {
    const key = t.stage ?? 'none';
    if (!map.has(key)) map.set(key, { key, label: t.stage ? `Etapa ${t.stage}` : 'Sem etapa', color: t.stage ? t.color : 'var(--border-strong)', items: [] });
    map.get(key).items.push(t);
  });
  return [...map.values()].sort((a, b) => (a.key === 'none' ? 99 : a.key) - (b.key === 'none' ? 99 : b.key));
}

function dayCount(n) {
  if (n == null) return '';
  if (n === 0) return 'hoje';
  if (n < 0) return 'já passou';
  return n === 1 ? 'amanhã' : `em ${n} dias`;
}

/** Índice do último nó alcançado (concluído ou o de hoje): o trilho fica lime até ali. */
function reachedUntil(stages) {
  let last = -1;
  stages.forEach((s, i) => {
    if (s.status === 'done' || s.today || s.status === 'late') last = i;
  });
  return last;
}

function MapNode({ stage: s, first, last, reached, onOpen }) {
  const done = s.status === 'done';
  const marker = {
    start: <MapPin size={15} aria-hidden="true" />,
    stage: s.number,
    final: <Repeat2 size={15} aria-hidden="true" />,
    exam: <Flag size={15} aria-hidden="true" />,
    after: <Award size={15} aria-hidden="true" />,
  }[s.kind];
  const markTone = s.kind === 'after' || s.kind === 'start' || done ? 'ring' : s.kind === 'exam' ? 'lime' : 'color';
  const eyebrow = s.kind === 'start'
    ? ['Ponto de partida', s.totalMinutes ? fmtHoursRound(s.totalMinutes) : null].filter(Boolean).join(' · ')
    : s.kind === 'final' ? `Reta final · ${s.meta}` : s.meta;
  const parts = s.kind !== 'start' && s.parts ? Object.entries(s.parts) : [];
  const hasMeta = (s.topics?.length > 0) || (s.note && s.kind !== 'start');
  const Tag = onOpen ? 'button' : 'div';
  return (
    <li className={`trv-node ${reached ? 'is-reached' : ''} ${first ? 'is-first' : ''} ${last ? 'is-last' : ''}`}>
      <span className="trv-node__rail" aria-hidden="true">
        <span
          className={`trv-mark trv-mark--${markTone}`}
          style={markTone === 'color' ? { background: s.color } : undefined}
        >
          {marker}
        </span>
      </span>
      <Tag
        type={onOpen ? 'button' : undefined}
        className={`trv-node__card ${s.today ? 'is-today' : ''} ${onOpen ? 'is-link' : ''}`}
        onClick={onOpen || undefined}
      >
        <span className="trv-node__head">
          <span className="trv-node__titles">
            {eyebrow && <span className="trv-node__eyebrow">{eyebrow}</span>}
            <strong className="trv-node__title">{s.title}</strong>
          </span>
          <span className="trv-node__badges">
            {parts.length > 0 && (
              <span className="trv-steps">
                {parts.map(([part, state]) => {
                  const st = PART_STATUS[state] || PART_STATUS.pending;
                  const tip = state === 'locked' ? unlockHint(s.parts, part) : st.label;
                  return (
                    <span key={part} className={`tr-part tr-part--${st.tone}`} title={tip} data-tour={part === 'checkpoint' ? 'trilha-checkpoint' : undefined}>
                      <st.icon size={11} strokeWidth={2.4} aria-hidden="true" />
                      {PARTS[part]?.label || part}
                      <span className="trv-sr">: {tip}</span>
                    </span>
                  );
                })}
              </span>
            )}
            {s.today && <span className="trv-today">Hoje</span>}
            {s.status && s.kind !== 'after' && <StatusPill status={s.status} />}
          </span>
        </span>

        {s.kind === 'start' && s.resources?.length > 0 && (
          <span className="trv-node__res">
            {s.resources.map(r => (
              <span key={r.id}>
                <SealPill seal={r.seal} />
                <span className="trv-node__rtitle">{r.title}</span>
                {r.minutes > 0 && <span className="trv-node__rmin">{fmtHours(r.minutes)}</span>}
                {r.done && <Check size={14} className="trv-node__rdone" aria-label="feito" />}
              </span>
            ))}
          </span>
        )}

        {hasMeta && (
          <span className="trv-node__meta">
            {s.topics?.map(t => (
              <span key={t.name} className="trv-topic">
                <span className="trv-sq" style={{ background: s.color }} />
                {t.name}
                <code>{t.label}</code>
              </span>
            ))}
            {s.note && s.kind !== 'start' && <span className="trv-node__note">{s.note}</span>}
          </span>
        )}

        {s.lateText && <span className="trv-node__late">{s.lateText}</span>}
        {s.today && s.totalMinutes > 0 && (
          <span className="trv-node__progress">
            <span className="trv-node__bar"><span style={{ width: `${Math.round((s.doneMinutes / s.totalMinutes) * 100)}%` }} /></span>
            <span>{fmtHours(s.doneMinutes)} de {fmtHours(s.totalMinutes)}</span>
          </span>
        )}
      </Tag>
    </li>
  );
}

export default TrailView;
